import { Box, Typography, IconButton } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useState, useEffect } from 'react';
import { Calendar, dayjsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './CalenderView.css';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import minMax from 'dayjs/plugin/minMax';
import weekday from 'dayjs/plugin/weekday';
import OneOnOneIcon from '../../../assets/Dashboard/oneoneonesession.svg';
import GroupIcon from '../../../assets/Dashboard/group.svg';
import PrimeIcon from '../../../assets/Dashboard/prime.svg';
import SmartPauseIcon from '../../../assets/SessionIcon/smart-pause-icon.svg';
import { setupCalendarRefresh, cleanupCalendarRefresh } from '../../../utils/calendarRefresh';
import PausedCalendarIcon from '../../../assets/ScheduleIcon/paused-calendar.svg';

// Register required dayjs plugins
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(localizedFormat);
dayjs.extend(minMax);
dayjs.extend(weekday);

// Create dayjs localizer for react-big-calendar
const localizer = dayjsLocalizer(dayjs);

// Define custom view types
const VIEWS = {
  WEEK: 'week',
};

// Session type styling mapping
const SessionTypeStyles = {
  one_on_one: {
    color: '#6B60E7',
    bgcolor: '#E8F0FF',
    value: 'One-On-One',
  },
  group: {
    color: '#7BCF8C',
    bgcolor: '#FAFFFB',
    value: 'Group',
  },
  prime: {
    color: '#AD8B00',
    bgcolor: '#F0EFFF',
    value: 'Prime',
  },
  smart_pause: {
    color: '#1F435D',
    bgcolor: '#F0F0F0',
    value: 'Smart Pause',
  },
  booked: {
    color: '#FF6B6B',
    bgcolor: '#FFF0F0',
    value: 'Booked',
  },
};

const CalendarView = ({
  currentDate,
  onPrevWeek,
  onNextWeek,
  onSessionClick = () => {},
  sessionsData = [],
  pauseSessionsData = [],
  bookedSessionsData = [],
  onRefreshRequest = null, // Optional callback to request fresh data from parent
}) => {
  // Use the provided current date or default to today
  const [date, setDate] = useState(currentDate || new Date());
  const [today] = useState(new Date()); // Store today's date for highlighting
  const [events, setEvents] = useState([]);
  console.log(bookedSessionsData, 'bookedSessionsData');

  // Process availability data into calendar events
  useEffect(() => {
    const processedEvents = [];
    let id = 1;

    // Helper function to convert time string to Date object parts
    const parseTime = timeStr => {
      const [hours, minutes] = timeStr.split(':').map(Number);
      return { hours, minutes };
    };

    // Helper function to handle events that cross midnight
    const createEvent = (
      eventDate,
      startHours,
      startMinutes,
      endHours,
      endMinutes,
      sessionType,
      sessionId,
      originalData
    ) => {
      // Check if event crosses midnight
      const crossesMidnight =
        endHours < startHours || (endHours === startHours && endMinutes < startMinutes);

      if (crossesMidnight) {
        // First part - from start time to midnight (23:59:59)
        processedEvents.push({
          id: `${sessionId}-${id++}-part1`,
          sessionId: sessionId,
          title: SessionTypeStyles[sessionType]?.value || sessionType,
          start: eventDate.hour(startHours).minute(startMinutes).second(0).toDate(),
          end: eventDate.hour(23).minute(59).second(59).toDate(),
          type: sessionType,
          color: SessionTypeStyles[sessionType]?.color,
          originalData: originalData,
          isContinued: true, // This session continues to next day
        });

        // Second part - from midnight (00:00:00) to end time on next day
        const nextDay = eventDate.add(1, 'day');
        processedEvents.push({
          id: `${sessionId}-${id++}-part2`,
          sessionId: sessionId,
          title: SessionTypeStyles[sessionType]?.value || sessionType,
          start: nextDay.hour(0).minute(0).second(0).toDate(),
          end: nextDay.hour(endHours).minute(endMinutes).second(0).toDate(),
          type: sessionType,
          color: SessionTypeStyles[sessionType]?.color,
          originalData: originalData,
          isContinuation: true, // This session is continued from previous day
        });
      } else {
        // Regular event (doesn't cross midnight)
        processedEvents.push({
          id: `${sessionId}-${id++}`,
          sessionId: sessionId,
          title: SessionTypeStyles[sessionType]?.value || sessionType,
          start: eventDate.hour(startHours).minute(startMinutes).second(0).toDate(),
          end: eventDate.hour(endHours).minute(endMinutes).second(0).toDate(),
          type: sessionType,
          color: SessionTypeStyles[sessionType]?.color,
          originalData: originalData,
        });
      }
    };

    // Process recurring availability slots
    sessionsData.forEach(session => {
      if (session.availability && session.availability.length > 0) {
        // Handle recurring weekly slots
        session.availability.forEach(slot => {
          slot.days.forEach(day => {
            console.log('slot', slot);
            // Get date for this day in the current week
            const dayOfWeek = [
              'Sunday',
              'Monday',
              'Tuesday',
              'Wednesday',
              'Thursday',
              'Friday',
              'Saturday',
            ].findIndex(d => d === day);

            if (dayOfWeek !== -1) {
              // Get the date for this day in the current week
              const eventDate = dayjs(date).day(dayOfWeek);

              // Parse times directly without conversion
              const startLocal = parseTime(slot.startTime);
              const endLocal = parseTime(slot.endTime);

              // Create event, handling midnight crossing
              createEvent(
                eventDate,
                startLocal.hours,
                startLocal.minutes,
                endLocal.hours,
                endLocal.minutes,
                session.session_type,
                session.id,
                session
              );
            }
          });
        });
      }

      // Process specific date overrides
      if (session.overrides && session.overrides.length > 0) {
        session.overrides.forEach(override => {
          const overrideDate = dayjs(override.date);

          // Parse times directly without conversion
          const startLocal = parseTime(override.startTime);
          const endLocal = parseTime(override.endTime);

          // Only add if in the current week
          const startOfWeek = dayjs(date).startOf('week');
          const endOfWeek = dayjs(date).endOf('week');

          if (overrideDate.isSameOrAfter(startOfWeek) && overrideDate.isSameOrBefore(endOfWeek)) {
            // Create event, handling midnight crossing
            createEvent(
              overrideDate,
              startLocal.hours,
              startLocal.minutes,
              endLocal.hours,
              endLocal.minutes,
              session.session_type,
              session.id,
              session
            );
          }
        });
      }
    });

    // Process pause availability periods
    if (pauseSessionsData && pauseSessionsData.length > 0) {
      pauseSessionsData.forEach(pause => {
        // Use data directly without UTC conversion since it's already in local time
        const pauseStart = dayjs(pause.start);
        const pauseEnd = dayjs(pause.end);

        // Check each day in the current week
        const weekStart = dayjs(date).startOf('week');
        const weekEnd = dayjs(date).endOf('week');

        // Loop through each day in the current week view
        for (
          let currentDay = weekStart.clone();
          currentDay.isSameOrBefore(weekEnd, 'day');
          currentDay = currentDay.add(1, 'day')
        ) {
          // If this day is within the pause period
          if (currentDay.isBetween(pauseStart, pauseEnd, 'day', '[]')) {
            // Create a full-day pause event (from 00:00 to 23:59)
            processedEvents.push({
              id: `pause-${pause.id}-${currentDay.format('YYYY-MM-DD')}`,
              sessionId: pause.id,
              title: 'Smart Pause',
              start: currentDay.hour(0).minute(0).second(0).toDate(),
              end: currentDay.hour(23).minute(59).second(59).toDate(),
              type: 'smart_pause',
              color: SessionTypeStyles.smart_pause.color,
              originalData: pause,
              isPauseEvent: true, // Flag to identify pause events specifically
              allDay: false, // Handle it as a time-specific event
            });
          }
        }
      });
    }

    // Process booked sessions data
    if (bookedSessionsData && bookedSessionsData.length > 0) {
      bookedSessionsData.forEach(booking => {
        try {
          // Use data directly without UTC conversion since it's already in local time
          const bookingStart = dayjs(booking.start_time);

          // Calculate the end time based on session length
          const bookingEnd = bookingStart.clone().add(booking.length_in_minutes, 'minute');

          // Check if booking crosses midnight
          const startDay = bookingStart.format('YYYY-MM-DD');
          const endDay = bookingEnd.format('YYYY-MM-DD');
          const crossesMidnight = startDay !== endDay;

          // Only add if in the current week
          const startOfWeek = dayjs(date).startOf('week');
          const endOfWeek = dayjs(date).endOf('week');

          // Check if session is in current week
          if (
            bookingStart.isBetween(startOfWeek, endOfWeek, 'day', '[]') ||
            bookingEnd.isBetween(startOfWeek, endOfWeek, 'day', '[]')
          ) {
            if (crossesMidnight) {
              // First part - until midnight
              processedEvents.push({
                id: `booking-${booking.id}-part1`,
                sessionId: booking.id,
                title: booking.title || 'Booked',
                start: bookingStart.toDate(),
                end: bookingStart.endOf('day').toDate(), // End at 23:59:59
                type: 'booked',
                color: SessionTypeStyles.booked.color,
                originalData: booking,
                isBooked: true,
                time: bookingStart.format('h:mm A'),
                isContinued: true, // This session continues to next day
              });

              // Process each additional day if session spans multiple days
              let currentDay = bookingStart.clone().add(1, 'day').startOf('day');

              // Add safety counter to prevent infinite loops
              let safetyCounter = 0;
              const MAX_DAYS = 7; // Reasonable maximum for UI display

              // Loop through each intermediate day if the session spans more than two days
              while (currentDay.format('YYYY-MM-DD') !== endDay && safetyCounter < MAX_DAYS) {
                processedEvents.push({
                  id: `booking-${booking.id}-day-${currentDay.format('YYYY-MM-DD')}`,
                  sessionId: booking.id,
                  title: booking.title || 'Booked',
                  start: currentDay.startOf('day').toDate(), // Start at 00:00
                  end: currentDay.endOf('day').toDate(), // End at 23:59:59
                  type: 'booked',
                  color: SessionTypeStyles.booked.color,
                  originalData: booking,
                  isBooked: true,
                  time: bookingStart.format('h:mm A'),
                  isContinuation: true,
                  isContinued: true,
                });

                // Make sure to create a new dayjs object to avoid mutation issues
                currentDay = currentDay.clone().add(1, 'day');
                safetyCounter++;
              }

              // Last part - from midnight to end on the final day
              processedEvents.push({
                id: `booking-${booking.id}-part-last`,
                sessionId: booking.id,
                title: booking.title || 'Booked',
                start: bookingEnd.startOf('day').toDate(), // Start at 00:00 of end day
                end: bookingEnd.toDate(), // End at the actual end time
                type: 'booked',
                color: SessionTypeStyles.booked.color,
                originalData: booking,
                isBooked: true,
                time: bookingStart.format('h:mm A'),
                isContinuation: true, // This session is continued from previous day
              });
            } else {
              // Regular session (doesn't cross midnight)
              processedEvents.push({
                id: `booking-${booking.id}`,
                sessionId: booking.id,
                title: booking.title || 'Booked',
                start: bookingStart.toDate(),
                end: bookingEnd.toDate(),
                type: 'booked',
                color: SessionTypeStyles.booked.color,
                originalData: booking,
                isBooked: true,
                time: bookingStart.format('h:mm A'),
              });
            }
          }
        } catch (error) {
          console.error('Error processing booked session:', error, booking);
        }
      });
    }

    setEvents(processedEvents);
  }, [date, sessionsData, pauseSessionsData, bookedSessionsData]);

  // Add useEffect to set initial scroll position
  useEffect(() => {
    // Set initial scroll position to show morning hours
    const timeContentElement = document.querySelector('.rbc-time-content');
    if (timeContentElement) {
      timeContentElement.scrollTop = 420; // Position to show morning hours
    }
  }, []);

  // Update local date when parent component changes currentDate
  useEffect(() => {
    if (currentDate) {
      setDate(currentDate);
    }
  }, [currentDate]);

  // Handle session click
  const handleEventClick = event => {
    onSessionClick(event.originalData || event.sessionId);
  };

  // Check if a date is today
  const isToday = dateToCheck => {
    return dayjs(dateToCheck).isSame(dayjs(today), 'day');
  };

  // Custom components for calendar
  const components = {
    // Custom event component to style events according to their type
    event: ({ event }) => {
      // Determine which icon to show based on event type
      let iconSrc = null;
      switch (event.type) {
        case 'one_on_one':
          iconSrc = OneOnOneIcon;
          break;
        case 'group':
          iconSrc = GroupIcon;
          break;
        case 'prime':
          iconSrc = PrimeIcon;
          break;
        case 'smart_pause':
          iconSrc = SmartPauseIcon;
          break;
        default:
          iconSrc = null;
      }

      // Calculate event duration in minutes for styling
      const startTime = dayjs(event.start);
      const endTime = dayjs(event.end);
      const durationInMinutes = endTime.diff(startTime, 'minute');

      // Adjust styles for small events
      const smallEvent = durationInMinutes < 30;

      // For smart pause events, render with PausedCalendarIcon as background
      if (event.type === 'smart_pause') {
        return (
          <div
            className="klasscal-paused-event"
            onClick={() => handleEventClick(event)}
            title="Unavailable (Smart Pause)"
            style={{
              backgroundImage: `url(${PausedCalendarIcon})`,
              backgroundRepeat: 'repeat',
              width: '100%',
              height: '100%',
              position: 'absolute',
              top: 0,
              left: 0,
              zIndex: 1,
              opacity: 1,
            }}
          />
        );
      }

      // Determine CSS classes based on event properties
      const cssClasses = [
        'klasscal-custom-event',
        `klasscal-${event.type}`,
        smallEvent ? 'klasscal-small-event' : '',
        event.isContinued ? 'klasscal-continued-event' : '',
        event.isContinuation ? 'klasscal-continuation-event' : '',
      ]
        .filter(Boolean)
        .join(' ');

      // For all other event types
      return (
        <div
          className={cssClasses}
          style={{
            backgroundColor: event.color,
            zIndex: 2, // Higher z-index to appear above paused events
          }}
          onClick={() => handleEventClick(event)}
        >
          {iconSrc && durationInMinutes >= 30 && (
            <div className="klasscal-event-icon">
              <img src={iconSrc || '/placeholder.svg'} alt={event.type} width="16" height="16" />
            </div>
          )}
          {event.isBooked && durationInMinutes >= 45 && (
            <div className="klasscal-booked-time">
              {startTime.format('h:mm')}
              {durationInMinutes > 60 ? `-${endTime.format('h:mm')}` : ''}
            </div>
          )}
        </div>
      );
    },
    // Custom header to match the design
    header: ({ date: headerDate, localizer }) => (
      <div className="klasscal-custom-header">
        <span className="klasscal-day-name">
          {localizer.format(headerDate, 'ddd').toUpperCase()}
        </span>
        <span
          className={`klasscal-day-number ${isToday(headerDate) ? 'klasscal-current-day' : ''}`}
          onClick={isToday(headerDate) ? goToToday : undefined}
          style={isToday(headerDate) ? { cursor: 'pointer' } : {}}
        >
          {localizer.format(headerDate, 'D')}
        </span>
      </div>
    ),
  };

  // Custom formats for dates and times
  const formats = {
    timeGutterFormat: (date, culture, localizer) => localizer.format(date, 'hh A', culture),
  };

  // Function to navigate to today
  const goToToday = () => {
    const currentDay = new Date();
    setDate(currentDay);
    if (typeof onPrevWeek === 'function' || typeof onNextWeek === 'function') {
      // Notify parent component if needed
      const parentCallback = onPrevWeek || onNextWeek;
      parentCallback(currentDay);
    }
  };

  // Create a refresh function that will trigger data refresh
  const refreshCalendarData = () => {
    // If parent component provided refresh callback, call it
    if (typeof onRefreshRequest === 'function') {
      onRefreshRequest();
    }
    // Otherwise just re-process the existing data
    else {
      // Force a re-render by updating state
      setEvents([...events]);
    }
  };

  // Set up the refresh function when component mounts
  useEffect(() => {
    setupCalendarRefresh(refreshCalendarData);

    // Clean up when component unmounts
    return () => {
      cleanupCalendarRefresh();
    };
  }, [sessionsData, pauseSessionsData, bookedSessionsData]); // Re-setup when data changes

  return (
    <Box className="klasscal-calendar-view">
      {/* Custom toolbar with date display and navigation */}
      <Box className="klasscal-calendar-top-nav">
        <Box className="klasscal-month-display">
          <Typography
            className="klasscal-current-month"
            component="button"
            onClick={goToToday}
            style={{
              cursor: 'pointer',
              border: 'none',
              background: 'none',
              padding: 0,
            }}
          >
            {dayjs(date).format('MMM YYYY')}
          </Typography>
        </Box>
        <Box className="klasscal-nav-buttons">
          {/* Today button with current date */}
          <Typography component="button" onClick={goToToday} className="klasscal-today-button">
            {dayjs(today).format('DD MMM')}
          </Typography>
          <IconButton className="klasscal-nav-button" onClick={onPrevWeek}>
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
          <IconButton className="klasscal-nav-button" onClick={onNextWeek}>
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <div className="klasscal-calendar-container">
        <Calendar
          localizer={localizer}
          events={events}
          defaultView={VIEWS.WEEK}
          views={[VIEWS.WEEK]}
          step={60}
          timeslots={1}
          date={date}
          onNavigate={setDate}
          min={new Date(0, 0, 0, 0, 0)} // Start at 00:00 (midnight)
          max={new Date(0, 0, 0, 23, 59, 59)} // End at 23:59:59 (just before midnight)
          formats={formats}
          components={components}
          toolbar={false} // Using custom toolbar
          startAccessor="start"
          endAccessor="end"
          onSelectEvent={handleEventClick}
          dayLayoutAlgorithm="no-overlap" // Handle overlapping events
          showMultiDayTimes={true} // Properly display events spanning multiple days
          scrollToTime={new Date(0, 0, 0, 7, 0)} // Scroll to 7am by default
        />
      </div>

      {/* Legend */}
      <Box className="klasscal-calendar-legend">
        <Box className="klasscal-legend-item">
          <Box
            className="klasscal-legend-color"
            style={{ backgroundColor: SessionTypeStyles.one_on_one.color }}
          ></Box>
          <Typography className="klasscal-legend-text">One On One</Typography>
        </Box>
        <Box className="klasscal-legend-item">
          <Box
            className="klasscal-legend-color"
            style={{ backgroundColor: SessionTypeStyles.group.color }}
          ></Box>
          <Typography className="klasscal-legend-text">Group</Typography>
        </Box>
        <Box className="klasscal-legend-item">
          <Box
            className="klasscal-legend-color"
            style={{ backgroundColor: SessionTypeStyles.prime.color }}
          ></Box>
          <Typography className="klasscal-legend-text">Prime</Typography>
        </Box>
        <Box className="klasscal-legend-item">
          <Box
            className="klasscal-legend-color"
            style={{ backgroundColor: SessionTypeStyles.smart_pause.color }}
          ></Box>
          <Typography className="klasscal-legend-text">Unavailable</Typography>
        </Box>
        <Box className="klasscal-legend-item">
          <Box
            className="klasscal-legend-color"
            style={{ backgroundColor: SessionTypeStyles.booked.color }}
          ></Box>
          <Typography className="klasscal-legend-text">Booked</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default CalendarView;
